<template>
  <v-expansion-panels>
    <v-expansion-panel>
      <v-expansion-panel-header color="#FFD683" class="p-6">
        Important information for first time users of on-line ordering
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <p class="heading mt-4">Using this system</p>
        <p>
          This form can be used to request:
        </p>
        <ul>
          <li>
            Single gene test for
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/fragilex-syndrome/"
              >fragile X syndrome</a
            >
          </li>
          <li>
            Chromosome
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/microarray-postnatal/"
              >microarray</a
            >
          </li>
          <li>
            Single gene test for
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/angelman-syndrome/"
              >Prader Willi / Angelman syndrome</a
            >
          </li>
          <li>
            Sanger sequencing for
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/cascade-family-testing/"
              >familial variant detection</a
            >
          </li>
          <li>
            Clinical
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/exome-sequencing/"
              >exome</a
            >
          </li>
          <li>
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/alport-syndrome/"
              >Alport syndrome</a
            >
          </li>
          <li>
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/whole-genome-sequencing/"
              >Whole genome sequencing</a
            >
          </li>
          <li>
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/kidney-disease/"
              >Heritable kidney disease (non-cystic, non-alport)</a
            >
          </li>
          <li>
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/kidney-disease/"
              >Heritable cystic kidney disease</a
            >
          </li>
          <li>
            <a target="_blank" href="https://www.vcgs.org.au/tests/connexin/"
              >Connexin / DFNB1</a
            >
          </li>
          <li>
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/deafness-mlpa/"
              >Deafness MLPA</a
            >
          </li>
          <li>
            <a target="_blank" href="https://www.vcgs.org.au/tests/phox2b/"
              >PHOX2B poly-alanine expansion testing</a
            >
          </li>
          <li>
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/spinocerebellar-ataxia-15/"
              >Spinocerebellar ataxia 15</a
            >
          </li>
          <li>
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/deafness-mlpa/"
              >Exome focused + deafness MLPA bundle</a
            >
          </li>
          <li>
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/cascade-family-testing/"
              >Mitochondrial DNA cascade & familial variant testing</a
            >
          </li>
          <li>
            <a target="_blank" href="https://www.vcgs.org.au/tests/mito-DNA/"
              >Whole mitochondrial genome sequencing (mtDNA)</a
            >
          </li>
          <li>
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/prenatal-diagnosis/"
              >Prenatal rapid genomic trio</a
            >
          </li>
          <li>
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/prenatal-diagnosis/"
              >Prenatal targeted exome (familial variant testing)</a
            >
          </li>
          <li>
            Prenatal targeted whole genome sequencing (WGS) (familial variant
            testing)
          </li>
          <li>
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/mitochondrial-diseases/"
              >Mitochondrial disease - diagnostic WGS testing</a
            >
          </li>
        </ul>
        <p class="mt-4">
          For other tests, please contact us at
          <a href="mailto:vcgs@vcgs.org.au">vcgs@vcgs.org.au</a> or
          <a href="tel:+611300118247">1300 118 247</a>.
        </p>
        <p>There are three sections to this request form:</p>
        <ol>
          <li>Doctor details</li>
          <li>Patient details</li>
          <li>Test selection</li>
        </ol>
        <p class="mt-4">
          Once started, requests must be completed.<br />
          Doctor details is the only section that can be saved for future use.
        </p>
        <p class="heading mt-8">Information required</p>
        <p class="mt-4" style="font-weight: bold;">For all test types</p>
        <ul>
          <li>Practitioner and clinic details, including email for reports</li>
          <li>Patient and parent details (where relevant)</li>
          <li>Funding source (eg. Medicare, patient/family, health service)</li>
        </ul>
        <p class="mt-4" style="font-weight: bold;">
          Additional details for genomic testing
        </p>
        <ul>
          <li>
            Clinical details – dysmorphic features, congenital abnormalities,
            measurements such as head circumference
          </li>
          <li>
            Details of previous testing – such as microarray results (microarray
            is required prior to Medicare funded childhood syndrome exomes)
          </li>
          <li>Family history, ethnicity, consanguinity</li>
        </ul>
        <p class="mt-4" style="font-weight: bold;">
          For Paediatricians ordering Medicare exome tests:
        </p>
        <ul>
          <li>
            After you have submitted the order, it will be reviewed by a VCGS
            laboratory clinical geneticist for approval.
          </li>
          <li>
            If the order is approved, you will receive a prompt regarding
            consent.
          </li>
        </ul>
        <p class="mt-4" style="font-weight: bold;">
          Informed consent for genomic testing
        </p>
        <ul>
          <li>
            You will need to confirm you have obtained informed consent for
            sequencing of patient DNA (and parents when a trio is ordered). This
            is mandatory before the test can proceed.
            <ClinexConsentFormsFragment />
            <span style="font-size: small;"
              >(consent obtained through a different clinical genetics service
              is acceptable)</span
            >
          </li>
        </ul>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import ClinexConsentFormsFragment from "@/components/common/ClinexConsentFormsFragment";

export default {
  components: { ClinexConsentFormsFragment },
};
</script>

<style>
.heading {
  color: #00538b;
  font-weight: bold;
}
</style>
